import { Popover, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, { Fragment } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { AnchorText, Grid } from '../../common';

const StyledNavListItems = styled.li`
  ${tw`text-lg font-semibold transition-all duration-200 cursor-pointer`}
  color: ${(props) => props.$color} !important;
  &:hover {
    color: ${(props) => props.$colorHover} !important;
  }
  & svg {
    ${tw`w-4 h-4`}
  }
  & a {
    color: ${(props) => props.$color} !important;
    ${tw`text-lg font-semibold transition-all duration-200`}
    &:hover {
      color: ${(props) => props.$colorHover} !important;
    }
  }
`;

const StyledDropdownContainer = styled.div`
  ${tw`overflow-hidden rounded-lg shadow-lg ring-1 ring-gray-800 ring-opacity-5`}
`;

const StyledDropdownItem = styled.div`
  color: ${(props) => props.$color};
  background-color: ${(props) => props.$bgColor};
  &:hover {
    color: ${(props) => props.$colorHover};
    background-color: ${(props) => props.$bgColorHover} !important;
  }
`;

const NavbarPrimaryDesktopDropdownNavItem = ({ nav, page, sanity }) => {
  const { colors } = sanity;
  // Determine the dropdown label:
  // If an explicit text label is provided, use it.
  // Otherwise, if a dropdown label link exists, use its name.
  const dropdownTextLabel = nav?.dropdownTextLabel;
  const dropdownLabelLink = nav?.dropdownLabelLink;
  const label =
    dropdownTextLabel || (dropdownLabelLink ? dropdownLabelLink.name : '');

  return (
    <Popover className="relative" key={label}>
      {({ open }) => (
        <>
          <Popover.Button>
            <StyledNavListItems
              className="flex center gap-x-2"
              $color={colors?.neutral?.dark}
              $colorHover={colors?.accent?.default}
            >
              {dropdownLabelLink && dropdownLabelLink?.slug?.current ? (
                <AnchorText
                  sanity={sanity}
                  type="internal"
                  color={colors?.neutral?.dark}
                  to={dropdownLabelLink.slug.current}
                >
                  {label}
                </AnchorText>
              ) : (
                <span style={{ color: colors?.neutral?.dark }}>{label}</span>
              )}
              <ChevronDownIcon
                aria-hidden="true"
                className={`${open ? 'rotate-180 transform' : ''} mt-1`}
              />
            </StyledNavListItems>
          </Popover.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel
              className={`absolute left-1/2 z-10 mt-3 w-screen max-w-sm -translate-x-1/2 transform px-4 sm:px-0 ${
                nav?.dropdown?.length < 4
                  ? 'lg:max-w-md'
                  : nav?.dropdown?.length < 10
                    ? 'lg:max-w-4xl'
                    : 'lg:max-w-7xl'
              }`}
            >
              <StyledDropdownContainer className="bg-white p-7">
                <Grid
                  classes={`relative gap-8 bg-white p-7 ${
                    nav?.dropdown?.length < 4
                      ? 'lg:grid-cols-1'
                      : nav?.dropdown?.length < 10
                        ? 'lg:grid-cols-2'
                        : 'lg:grid-cols-4'
                  }`}
                >
                  {nav?.dropdown?.map((dropdownItem) => (
                    <StyledNavListItems
                      key={dropdownItem?.singlePageLink?.slug?.current}
                      $color={colors?.neutral?.dark}
                      $colorHover={colors?.accent?.default}
                    >
                      <AnchorText
                        sanity={sanity}
                        type="internal"
                        color={colors?.neutral?.default}
                        to={dropdownItem?.singlePageLink?.slug?.current}
                      >
                        <StyledDropdownItem
                          className="flex p-2 transition duration-150 ease-in-out rounded-lg "
                          $bgColorHover={colors?.accent?.lighter}
                        >
                          <div>
                            <GatsbyImage
                              image={
                                dropdownItem?.singlePageLink?.heroBgImg?.asset
                                  ?.gatsbyImageData
                              }
                              alt={dropdownItem?.singlePageLink?.heroBgImg?.alt}
                              loading="eager"
                              className="flex items-center content-center justify-start w-16 h-16 rounded-lg shadow-lg"
                            />
                          </div>
                          <div className="ml-4">
                            <p className="text-lg font-medium text-gray-900">
                              {dropdownItem?.singlePageLink?.name}
                            </p>
                            <p className="text-sm font-normal text-gray-500">
                              {dropdownItem?.singlePageLink?.subtitle}
                            </p>
                          </div>
                        </StyledDropdownItem>
                      </AnchorText>
                    </StyledNavListItems>
                  ))}
                </Grid>
                {/* Footer Link */}
                {nav?.dropdownFooterLink &&
                  nav?.dropdownFooterLink?.slug?.current && (
                    <div className="pt-4 mt-4 border-t border-gray-200">
                      <AnchorText
                        sanity={sanity}
                        type="internal"
                        weight="600"
                        // color={colors?.neutral?.dark}
                        colorHover={colors?.accent?.default}
                        to={nav?.dropdownFooterLink.slug.current}
                        className="text-sm !font-bold"
                      >
                        {nav?.dropdownFooterLinkAnchorText || 'Learn More'}
                      </AnchorText>
                    </div>
                  )}
              </StyledDropdownContainer>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export { NavbarPrimaryDesktopDropdownNavItem };
